import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  useSubscription,
  PricingPlan,
} from '../../contexts/SubscriptionContext';
import styles from './SubscriptionModal.module.scss';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPlan?: PricingPlan;
  isYearly?: boolean;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  selectedPlan,
  isYearly = false,
}) => {
  const { createCheckoutSession } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !selectedPlan) return null;

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);

    try {
      const priceId = isYearly
        ? selectedPlan.yearlyPriceId
        : selectedPlan.monthlyPriceId;
      const { url } = await createCheckoutSession(
        priceId,
        window.location.href
      );

      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(
        err.response?.data?.message ||
          'Failed to start subscription process. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const price = isYearly ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice;
  const originalPrice = isYearly ? selectedPlan.monthlyPrice * 12 : null;
  const savings = originalPrice ? originalPrice - price : 0;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Subscribe to {selectedPlan.name}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.planSummary}>
          <div className={styles.planInfo}>
            <h3>{selectedPlan.name} Plan</h3>
            <p>{selectedPlan.description}</p>
          </div>

          <div className={styles.priceInfo}>
            <div className={styles.priceDisplay}>
              {isYearly && savings > 0 && (
                <span className={styles.originalPrice}>${originalPrice}</span>
              )}
              <span className={styles.currentPrice}>
                ${price}
                <span className={styles.period}>
                  /{isYearly ? 'year' : 'month'}
                </span>
              </span>
            </div>
            {isYearly && savings > 0 && (
              <div className={styles.savings}>Save ${savings} per year</div>
            )}
          </div>
        </div>

        <div className={styles.features}>
          <h4>What's included:</h4>
          <ul>
            {selectedPlan.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.modalActions}>
          <button
            className={styles.subscribeButton}
            onClick={handleSubscribe}
            disabled={loading}
          >
            {loading ? 'Processing...' : `Subscribe Now`}
          </button>
        </div>

        <div className={styles.secureNote}>
          Secure checkout powered by Stripe
        </div>
      </div>
    </div>
  );
};
